import ConfirmationModal from "components/ConfirmationModal";
import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { StatusChip } from "components/StatusChip";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useEffect, useState } from "react";
import { editCourtAppeal } from "services/advocateComplaintService";
import { AdvocateComplaints, CourtAppeal, Litigation } from "types";
import { ADVOCATE_COURT_APPEAL_STATUS } from "utils/constantValues";
import { DateView } from "utils/helper";
import { ADVOCATE_ROLES } from "utils/roles";
import AddCourtAppealResponse from "./AddCourtAppealResponse";
import AddCourtSummonRegistrationModal from "./AddCourtSummonRegistrationModal";
import CourtSummonConfirmationModal from "./CourtSummonConfirmationModal";

interface SummonProps {
  summonData: CourtAppeal;
  compliantData: AdvocateComplaints;
  appealLitigationData?: Litigation[];
  onSummonRefresh: any;
  onComplaintsRefresh: any;
}

export default function CourtSummonCard({
  summonData,
  compliantData,
  appealLitigationData,
  onSummonRefresh,
  onComplaintsRefresh,
}: SummonProps) {
  const { t } = useTranslation();
  const canCoordinatorConfirmSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);
  const canSendAccusedCourtToRegistrar = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canAddRegistrationNumber = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
  ]);
  const canAccusedConfirmSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canUpdateHearingDate = useHasRole([
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canSendAccusedSummonToCoordinator = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
  ]);

  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState<boolean>(false);
  const [coordinatorConfirmationModal, setCoordinatorConfirmationModal] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [isSendToCoordinatorModal, setIsSendToCoordinatorModal] =
    useState(false);
  const [issSendToRegistrar, setIsSendToRegistrar] = useState(false);
  const [registerModal, setRegisterModal] = useState<boolean>(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "details",
  ]);
  const [isEditingCourtHearingDate, setIsEditingCourtHearingDate] =
    useState(false);
  const [courtHearingDateDraft, setCourtHearingDateDraft] = useState("");
  const [isUpdatingCourtHearingDate, setIsUpdatingCourtHearingDate] =
    useState(false);

  const [canReassignProsecutor, setCanReassignProsecutor] = useState(false);

  useEffect(() => {
    if (appealLitigationData) {
      setCanReassignProsecutor(
        appealLitigationData?.find((a) =>
          a?.verdict_documents?.some(
            (v: { document_type: string }) =>
              v?.document_type === "council_decision_by_pro",
          ),
        )
          ? false
          : true,
      );
    }
  }, [appealLitigationData]);

  const handleApproveClick = async () => {
    if (!summonData) {
      showToast(t("courtSummonNotFound"), "error");
      return;
    }
    setLoading(true);

    const data = new FormData();

    data.append("status", ADVOCATE_COURT_APPEAL_STATUS.APPROVED);
    data.append("admin_unit_id", compliantData?.admin_unit_id || "");
    data.append("org_id", compliantData?.org_id || "");
    data.append("accused_advocate_name", compliantData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      compliantData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      compliantData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editCourtAppeal(data, summon_id, isFormData);

      onSummonRefresh();
      setShowConfirmationModal(false);
      showToast(t("successfullyConfirmed"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendToCoordinator = async () => {
    if (!summonData) {
      showToast(t("courtSummonNotFound"), "error");
      return;
    }
    setLoading(true);

    const data = new FormData();

    data.append(
      "status",
      ADVOCATE_COURT_APPEAL_STATUS.FORWARDED_TO_COORDINATOR,
    );
    data.append("admin_unit_id", compliantData?.admin_unit_id || "");
    data.append("org_id", compliantData?.org_id || "");
    data.append("accused_advocate_name", compliantData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      compliantData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      compliantData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editCourtAppeal(data, summon_id, isFormData);

      onSummonRefresh();
      setIsSendToCoordinatorModal(false);
      showToast(t("successful"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendToRegistrar = async () => {
    if (!summonData) {
      showToast(t("courtSummonNotFound"), "error");
      return;
    }
    setLoading(true);

    const data = new FormData();

    data.append("status", ADVOCATE_COURT_APPEAL_STATUS.FORWARDED_TO_REGISTRAR);
    data.append("admin_unit_id", compliantData?.admin_unit_id || "");
    data.append("org_id", compliantData?.org_id || "");
    data.append("accused_advocate_name", compliantData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      compliantData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      compliantData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editCourtAppeal(data, summon_id, isFormData);

      onSummonRefresh();
      setIsSendToRegistrar(false);
      showToast(t("successful"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((item) => item !== section)
        : [...prev, section],
    );
  };

  const toDateInputValue = (value?: string | null) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startCourtHearingDateEdit = () => {
    setCourtHearingDateDraft(toDateInputValue(summonData?.court_hearing_date));
    setIsEditingCourtHearingDate(true);
  };

  const cancelCourtHearingDateEdit = () => {
    setIsEditingCourtHearingDate(false);
    setCourtHearingDateDraft("");
  };

  const handleUpdateCourtHearingDate = async () => {
    if (!summonData?.id) {
      showToast(t("courtSummonNotFound"), "error");
      return;
    }

    const currentDate = toDateInputValue(summonData?.court_hearing_date);
    if (!courtHearingDateDraft || courtHearingDateDraft === currentDate) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingCourtHearingDate(true);
    try {
      await editCourtAppeal(
        {
          court_hearing_date: courtHearingDateDraft,
        } as any,
        summonData.id,
        false,
      );

      await Promise.resolve(onSummonRefresh?.());
      cancelCourtHearingDateEdit();
      showToast("Court hearing date updated", "success");
    } catch (err) {
      showToast("Failed to update court hearing date", "error");
    } finally {
      setIsUpdatingCourtHearingDate(false);
    }
  };

  if (!summonData) return null;

  // Sort appeal documents by created_at DESC and split into latest + history
  const sortedAppealDocs = [...(summonData?.appeal_documents || [])].sort(
    (a: any, b: any) =>
      new Date(b?.created_at ?? "").getTime() -
      new Date(a?.created_at ?? "").getTime(),
  );
  const latestAppealDoc = sortedAppealDocs?.[0];
  const appealDocHistory = sortedAppealDocs?.slice(1) || [];

  return (
    <div className="mb-8">
      {/* Header Section */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800 border-l-4 border-red-600 pl-3 py-1">
          {t("courtAppealSummon")}
        </h2>
      </div>

      {/* Main Container */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4">
        <div className="p-5">
          {/* Assigned Prosecutor */}
          {summonData?.assigned_prosecutor && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={require("../../../images/avatar.png")}
                    alt="avatar"
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {summonData?.assigned_prosecutor || "-"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("assignedProsecutor")}
                    </p>
                  </div>
                </div>
                <StatusChip status={summonData?.status ?? null} />
              </div>
            </div>
          )}

          {/* Latest Document */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                {t("appealDocuments")}
              </h3>
              {latestAppealDoc && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {t("latestVersion")}
                </span>
              )}
            </div>

            {latestAppealDoc ? (
              <DocumentViewerModal
                documents={[latestAppealDoc] as any}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                layout="list"
              />
            ) : (
              <div className="text-center py-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                <p className="text-sm text-gray-600">
                  {t("noAppealDocumentsUploaded")}
                </p>
              </div>
            )}
          </div>

          {/* Registration Details */}
          {(summonData?.registration_date ||
            summonData?.registration_number) && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {summonData?.registration_date && (
                  <div>
                    <p className="text-xs text-gray-500">
                      {t("registrationDate")}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {summonData.registration_date}
                    </p>
                  </div>
                )}
                {summonData?.registration_number && (
                  <div>
                    <p className="text-xs text-gray-500">
                      {t("registrationNumber")}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {summonData.registration_number}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expandable Sections */}
          <div className="space-y-4">
            {/* Court Details (static) */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection("details")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-700">
                      {t("courtDetails")}
                    </span>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.includes("details") ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {expandedSections.includes("details") && (
                <div className="p-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        {t("courtHearingDate")}
                      </p>
                      <div className="flex items-center gap-3">
                        <p className="text-sm font-medium text-gray-900">
                          {summonData?.court_hearing_date
                            ? DateView(summonData.court_hearing_date)
                            : "-"}
                        </p>
                        {canUpdateHearingDate && (
                          <button
                            type="button"
                            onClick={startCourtHearingDateEdit}
                            className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                          >
                            {t("updateDate")}
                          </button>
                        )}
                      </div>
                    </div>
                    {summonData?.court_name && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">
                          {t("courtName")}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {summonData.court_name}
                        </p>
                      </div>
                    )}
                    {summonData?.court_number && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">
                          {t("courtNumber")}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {summonData.court_number}
                        </p>
                      </div>
                    )}
                    {summonData?.hearing_court && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">
                          {t("hearingCourt")}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {summonData.hearing_court}
                        </p>
                      </div>
                    )}
                    {summonData?.court_order_summon && (
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-500 mb-1">
                          {t("courtOrder")}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {summonData.court_order_summon}
                        </p>
                      </div>
                    )}
                  </div>

                  {isEditingCourtHearingDate && (
                    <div className="mt-3 rounded-md border border-gray-200 bg-white p-3 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("courtHearingDate")}
                        </label>
                        <input
                          type="date"
                          value={courtHearingDateDraft}
                          onChange={(e) =>
                            setCourtHearingDateDraft(e.target.value)
                          }
                          className="w-full md:w-64 rounded border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={isUpdatingCourtHearingDate}
                          onClick={handleUpdateCourtHearingDate}
                          className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                        >
                          {isUpdatingCourtHearingDate
                            ? "Updating..."
                            : "Save Date"}
                        </button>
                        <button
                          type="button"
                          disabled={isUpdatingCourtHearingDate}
                          onClick={cancelCourtHearingDateEdit}
                          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Document History */}
            {appealDocHistory.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection("history")}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-700">
                        {t("documentHistory")}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({appealDocHistory.length} {t("previousVersions")})
                      </span>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.includes("history") ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {expandedSections.includes("history") && (
                  <div className="p-4 border-t border-gray-200">
                    <DocumentViewerModal
                      documents={appealDocHistory as any}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                      layout="list"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Response Summary */}
          {summonData?.response_summary && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {t("responseSummary")}
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {summonData.response_summary}
                </p>
              </div>
            </div>
          )}

          {/* Response Document */}
          {summonData?.response && (
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-red-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {summonData.response_file_details?.file_name ||
                        t("responseDocument")}
                    </p>
                  </div>
                </div>
                <FileActions fileUrl={summonData?.response || ""} />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 space-y-3">
            {/* Send to Coordinator Button */}
            {canSendAccusedSummonToCoordinator &&
              summonData?.status === ADVOCATE_COURT_APPEAL_STATUS.REVISION && (
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setIsSendToCoordinatorModal(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                  {t("sendToCoordinator")}
                </button>
              )}

            {/* Assign Prosecutor Button */}
            {canCoordinatorConfirmSummon &&
              summonData?.status ===
                ADVOCATE_COURT_APPEAL_STATUS.FORWARDED_TO_COORDINATOR &&
              !summonData?.assigned_prosecutor && (
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setCoordinatorConfirmationModal(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  {t("assignProsecutor")}
                </button>
              )}

            {/* Reassign Prosecutor Button */}
            {canCoordinatorConfirmSummon &&
              summonData?.assigned_prosecutor &&
              canReassignProsecutor && (
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setCoordinatorConfirmationModal(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {t("reassignProsecutor")}
                </button>
              )}

            {/* Add Response Button */}
            {canAccusedConfirmSummon &&
              summonData?.status ===
                ADVOCATE_COURT_APPEAL_STATUS.ASSIGNED_TO_PROSECUTOR &&
              !summonData?.response_summary && (
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setShowResponseModal(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  {t("addResponse")}
                </button>
              )}

            {/* Send to Registrar Button */}
            {canSendAccusedCourtToRegistrar &&
              summonData?.status ===
                ADVOCATE_COURT_APPEAL_STATUS.ASSIGNED_TO_PROSECUTOR &&
              summonData?.response_summary && (
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setIsSendToRegistrar(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                  {t("sendToRegistrar")}
                </button>
              )}

            {/* Add Registration Number Button */}
            {canAddRegistrationNumber &&
              summonData?.status ===
                ADVOCATE_COURT_APPEAL_STATUS.FORWARDED_TO_REGISTRAR &&
              !summonData?.registration_number && (
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setRegisterModal(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {t("addRegistrationNo")}
                </button>
              )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {summonData && compliantData && (
        <CourtSummonConfirmationModal
          isOpen={coordinatorConfirmationModal}
          setIsOpen={setCoordinatorConfirmationModal}
          complaintData={compliantData}
          summonData={summonData}
          onRefresh={onSummonRefresh}
          onComplaintsRefresh={onComplaintsRefresh}
        />
      )}

      {summonData && compliantData && (
        <AddCourtAppealResponse
          isOpen={showResponseModal}
          setIsOpen={setShowResponseModal}
          summonData={summonData}
          complaintData={compliantData}
          onRefresh={onSummonRefresh}
        />
      )}

      {summonData && compliantData && (
        <AddCourtSummonRegistrationModal
          isOpen={registerModal}
          setIsOpen={setRegisterModal}
          summonData={summonData}
          complaintData={compliantData}
          onRefresh={onSummonRefresh}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleApproveClick}
        isLoading={loading}
      />

      <ConfirmationModal
        isOpen={isSendToCoordinatorModal}
        onCancel={() => setIsSendToCoordinatorModal(false)}
        onConfirm={handleSendToCoordinator}
        isLoading={loading}
      />

      <ConfirmationModal
        isOpen={issSendToRegistrar}
        onCancel={() => setIsSendToRegistrar(false)}
        onConfirm={handleSendToRegistrar}
        isLoading={loading}
      />
    </div>
  );
}
